import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Mail, Sparkles, ArrowRight, CheckCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { supabase } from "@/integrations/supabase/client";
import { getStoredAffiliateCode } from "@/hooks/useAffiliateTracking";
import { sanitizeName, sanitizeEmail } from "@/lib/sanitize";

interface NewsletterFormProps {
  variant?: "full" | "compact";
  listType?: "newsletter" | "waitlist";
  colorScheme?: "dark" | "light";
}

export const NewsletterForm = ({ variant = "compact", listType = "newsletter", colorScheme = "dark" }: NewsletterFormProps) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email) {
      toast({ 
        title: t("footer.newsletterError"), 
        description: t("footer.newsletterFieldsRequired"),
        variant: "error" 
      });
      return;
    }
    
    if (!privacyAccepted) {
      toast({ 
        title: t("footer.newsletterError"), 
        description: t("footer.newsletterPrivacyRequired"),
        variant: "error" 
      });
      return;
    }

    setIsLoading(true);
    
    // Get affiliate code from localStorage
    const affiliateCode = getStoredAffiliateCode();
    
    try {
      const { error } = await supabase.functions.invoke("mailerlite-subscribe", {
        body: {
          email: sanitizeEmail(email),
          name: sanitizeName(name),
          listType,
          language: i18n.language || "it",
          affiliateCode: affiliateCode || undefined,
        },
      });

      if (error) {
        console.error("MailerLite subscription error:", error);
        toast({ 
          title: t("footer.newsletterError"), 
          description: t("footer.newsletterErrorDesc"),
          variant: "error" 
        });
      } else {
        setIsSubscribed(true);
        setEmail("");
        setName("");
        setPrivacyAccepted(false);
      }
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
      toast({ 
        title: t("footer.newsletterError"), 
        description: t("footer.newsletterErrorDesc"),
        variant: "error" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "full") {
    // Show confirmation message if subscribed
    if (isSubscribed) {
      return (
        <div className={`${colorScheme === "light" ? "bg-primary/20" : "bg-dark/50"} border-[3px] border-primary rounded-lg p-6 md:p-8`}>
          <div className="flex flex-col items-center justify-center text-center py-8 space-y-4">
            <div className={`p-4 ${colorScheme === "light" ? "bg-primary" : "bg-primary/20"} rounded-full`}>
              <CheckCircle className={`w-12 h-12 ${colorScheme === "light" ? "text-dark" : "text-primary"}`} />
            </div>
            <h2 className={`font-heading text-2xl font-bold ${colorScheme === "light" ? "text-dark" : "text-primary"}`}>
              {listType === "waitlist" 
                ? t("footer.waitlistSuccessTitle") 
                : t("footer.newsletterSuccessTitle")}
            </h2>
            <p className={`${colorScheme === "light" ? "text-dark/80" : "text-gray-300"} leading-relaxed max-w-md`}>
              {listType === "waitlist" 
                ? t("footer.waitlistSuccessDesc") 
                : t("footer.newsletterSuccessDesc")}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={`${colorScheme === "light" ? "bg-primary/20" : "bg-dark/50"} border-[3px] border-primary rounded-lg p-6 md:p-8`}>
        <div className="grid md:grid-cols-5 gap-8 items-center">
          {/* Left side - Text content (40%) */}
          <div className="md:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 ${colorScheme === "light" ? "bg-primary" : "bg-primary/20"} rounded-lg`}>
                <Mail className={`w-6 h-6 ${colorScheme === "light" ? "text-dark" : "text-primary"}`} />
              </div>
              <h2 className={`font-heading text-2xl font-bold ${colorScheme === "light" ? "text-dark" : "text-primary"}`}>
                {listType === "waitlist" 
                  ? t("footer.waitlistTitle") 
                  : t("footer.newsletterTitle")}
              </h2>
            </div>
            <p className={`${colorScheme === "light" ? "text-dark/80" : "text-gray-300"} leading-relaxed`}>
              {listType === "waitlist" 
                ? t("footer.waitlistSubtitle") 
                : t("footer.newsletterSubtitle")}
            </p>
            <div className={`flex items-center gap-2 text-sm ${colorScheme === "light" ? "text-dark/70" : "text-gray-400"}`}>
              <Sparkles className={`w-4 h-4 ${colorScheme === "light" ? "text-dark" : "text-primary"}`} />
              <span>
                {listType === "waitlist" 
                  ? t("footer.waitlistJoinCount") 
                  : t("footer.newsletterJoinCount")}
              </span>
            </div>
          </div>

          {/* Right side - Form (60%) */}
          <div className="md:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Name field */}
                <div className="space-y-2">
                  <label htmlFor="newsletter-name-full" className={`text-sm font-medium ${colorScheme === "light" ? "text-dark/80" : "text-gray-300"}`}>
                    {t("footer.newsletterNameLabel")}
                  </label>
                  <input
                    id="newsletter-name-full"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("footer.newsletterNamePlaceholder")}
                    className="w-full px-4 py-3 bg-white text-dark border-[3px] border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    required
                  />
                </div>
                
                {/* Email field */}
                <div className="space-y-2">
                  <label htmlFor="newsletter-email-full" className={`text-sm font-medium ${colorScheme === "light" ? "text-dark/80" : "text-gray-300"}`}>
                    {t("footer.newsletterEmailLabel")}
                  </label>
                  <input
                    id="newsletter-email-full"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("footer.newsletterEmailPlaceholder")}
                    className="w-full px-4 py-3 bg-white text-dark border-[3px] border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Privacy checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="newsletter-privacy-full"
                  checked={privacyAccepted}
                  onCheckedChange={(checked) => setPrivacyAccepted(checked === true)}
                  className={`mt-1 border-2 ${colorScheme === "light" ? "border-dark" : "border-primary"} data-[state=checked]:bg-primary data-[state=checked]:text-dark`}
                />
                <label htmlFor="newsletter-privacy-full" className={`text-sm ${colorScheme === "light" ? "text-dark/70" : "text-gray-300"} leading-relaxed cursor-pointer`}>
                  {t("footer.newsletterPrivacy")}{" "}
                  <Link to="/privacy" className="text-primary hover:underline font-medium">
                    {t("footer.newsletterPrivacyLink")}
                  </Link>{" "}
                  {t("footer.newsletterPrivacyAnd")}
                </label>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto px-8 py-3 bg-primary text-dark font-heading font-bold uppercase tracking-wider rounded-lg hover:bg-primary/90 hover:translate-y-[-2px] hover:shadow-lg hover:shadow-primary/30 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading 
                  ? t("common.loading") 
                  : listType === "waitlist" 
                    ? t("footer.waitlistSubscribe") 
                    : t("footer.newsletterSubscribe")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Compact variant
  return (
    <div className="max-w-xl mx-auto text-center">
      <h2 className="font-heading text-3xl font-bold mb-4">
        {t("home.newsletterTitle")}
      </h2>
      <p className="text-gray-700 mb-6">
        {t("home.newsletterDesc")}
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <BrutalInput
            type="text"
            placeholder={t("footer.newsletterNamePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <BrutalInput
            type="email"
            placeholder={t("home.newsletterPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        
        {/* Privacy checkbox */}
        <div className="flex items-start gap-3 text-left justify-center">
          <Checkbox
            id="newsletter-privacy-compact"
            checked={privacyAccepted}
            onCheckedChange={(checked) => setPrivacyAccepted(checked === true)}
            className="mt-1 border-dark data-[state=checked]:bg-primary data-[state=checked]:text-dark"
          />
          <label htmlFor="newsletter-privacy-compact" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
            {t("footer.newsletterPrivacy")}{" "}
            <Link to="/privacy" className="text-primary hover:underline font-medium">
              {t("footer.newsletterPrivacyLink")}
            </Link>{" "}
            {t("footer.newsletterPrivacyAnd")}
          </label>
        </div>

        <BrutalButton type="submit" disabled={isLoading}>
          {isLoading ? t("common.loading") : t("home.newsletterButton")}
        </BrutalButton>
      </form>
    </div>
  );
};
